import { useState } from "react";
import { useParams } from "react-router-dom";

import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { Button } from "@mui/material";

import { ChangeEntryKind } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import formatTimestamp from "@/common/utils/formatTimestamp";
import { formatUserCode } from "@/common/utils/formatUserCode";
import { ClientByIdParams } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

import { ChangeHistoryDialog } from "./ChangeHistoryDialog";
import { ChangeHistoryScope } from "./hooks/useChangeHistoryScope";
import { useClientChangeInfo } from "./hooks/useClientChangeInfo";

interface Props {
  scope: ChangeHistoryScope | undefined;
}

/**
 * The change-information line shown next to every save button of the client case file (Kundenakt): creation
 * and last change of what the view on screen shows — its section, or the single opened entry of it
 * (an Aktenvermerk, a Forderung) — clickable to open the detailed change-history overlay. Resolves
 * the client from the route; a page's scope comes from ClientRouteChangeInfo.
 */
export function ClientChangeInfo({ scope }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { id } = useParams<ClientByIdParams>();
  const { data } = useClientChangeInfo(id, scope);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  if (!id || !scope || !data?.createdAtUtc || !data.lastModifiedAtUtc) {
    return null;
  }

  // Only the most recent event, to stay single-line; the creation info remains visible as the oldest
  // row of the dialog. Worded after what that event was for the scope on screen, which the backend
  // resolves: a first collection entry is a change of its section, while the same event scoped to
  // that one entry — the opened case note (Aktenvermerk) — is its creation.
  const infoText =
    data.lastChangeKind === ChangeEntryKind.Created
      ? t("common.createdAtBy", {
          date: formatTimestamp(data.createdAtUtc, t, language),
          user: formatUserCode(data.createdByUserCode, t),
        })
      : t("common.lastChanged", {
          date: formatTimestamp(data.lastModifiedAtUtc, t, language),
          user: formatUserCode(data.lastModifiedByUserCode, t),
        });

  return (
    <>
      <Button
        variant="text"
        size="small"
        type="button"
        startIcon={<HistoryRoundedIcon fontSize="small" />}
        onClick={() => setIsHistoryOpen(true)}
        aria-haspopup="dialog"
        sx={{ textTransform: "none", color: "text.secondary", fontWeight: "normal", typography: "body2" }}
      >
        {infoText}
      </Button>
      {isHistoryOpen && <ChangeHistoryDialog clientId={id} scope={scope} onClose={() => setIsHistoryOpen(false)} />}
    </>
  );
}
